"use server";

import { normalizeKind } from "@/lib/tag-groups";
import { CACHE_TAGS } from "@/lib/cache";
import { defineAction } from "@/lib/define-action";
import { bestEffort } from "@/lib/errors";
import { prisma } from "@/lib/prisma";
import { findActiveReferences, formatActiveReferencesError } from "@/lib/deletion-guards";
import { revalidatePath } from "next/cache";
import { authorizeDeletion } from "@/lib/deletion-requests";
import { indexApplication, indexClient, removeApplication, removeClient } from "@/lib/search";
import { recordTeamActivity } from "@/lib/team-activity";
import { setClientArchived } from "@/lib/clients/archive";

type Fields = { description?: string; logoUrl?: string };
const fields = (name: string, o?: Fields) => ({ name: name.trim(), description: o?.description?.trim() || null, logoUrl: o?.logoUrl?.trim() || null });
const need = (name: string, label: string) => { if (!name.trim()) throw new Error(`${label} name is required`); };

export const createClient = defineAction({
  guard: "clients:manage",
  run: async ({ userId }, name: string, opts?: Fields) => {
    need(name, "Client");
    const created = await prisma.client.create({ data: { ...fields(name, opts), creator: userId } });
    await bestEffort(recordTeamActivity({ kind: "client", actorId: userId, label: created.name, link: `/clients/${created.id}` }), "record team activity");
    return created;
  },
  unique: { target: "Client_name_key", message: "A client with this name already exists." },
  tag: CACHE_TAGS.clients,
  paths: ["/clients"],
  index: indexClient,
});

export const updateClient = defineAction({
  guard: "clients:manage",
  run: async ({ userId }, id: number, input: { name: string } & Fields) => {
    need(input.name, "Client");
    return prisma.client.update({ where: { id }, data: { ...fields(input.name, input), updatedBy: userId } });
  },
  unique: { target: "Client_name_key", message: "A client with this name already exists." },
  tag: CACHE_TAGS.clients,
  paths: (_c, id: number) => ["/clients", `/clients/${id}`],
  index: indexClient,
});

type DeleteResult = { ok: true; requested?: true } | { ok: false; error: string };

export const deleteClient = defineAction({
  guard: (id: number, reason?: string) => authorizeDeletion("CLIENT", id, reason),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the guard reads the reason
  run: async (gate, id: number, _reason?: string): Promise<DeleteResult> => {
    if ("requested" in gate) return { ok: true, requested: true };
    const refs = await findActiveReferences("Client", id);
    if (refs.length > 0) return { ok: false, error: formatActiveReferencesError(refs) };
    await prisma.client.update({ where: { id }, data: { deletedAt: new Date(), deletedBy: gate.userId } });
    return { ok: true };
  },
  tag: CACHE_TAGS.clients,
  paths: (_o, id: number) => ["/clients", `/clients/${id}`],
  index: (o, id: number) => !o.requested && removeClient(id),
});

export const restoreClient = defineAction({
  guard: "trash:restore",
  run: async (_c, id: number) => {
    const row = await prisma.client.update({ where: { id }, data: { deletedAt: null, deletedBy: null } });
    await bestEffort(indexClient(row), "reindex restored client");
  },
  tag: CACHE_TAGS.clients,
  paths: (_o, id: number) => ["/clients", `/clients/${id}`, "/admin/trash"],
});

export const createApplication = defineAction({
  guard: "applications:manage",
  run: async ({ userId }, name: string, opts?: Fields) => {
    need(name, "Application");
    const created = await prisma.application.create({ data: { ...fields(name, opts), creator: userId } });
    await bestEffort(recordTeamActivity({ kind: "application", actorId: userId, label: created.name, link: `/applications/${created.id}` }), "record team activity");
    return created;
  },
  unique: { target: "Application_name_key", message: "An application with this name already exists." },
  tag: CACHE_TAGS.applications,
  paths: ["/applications"],
  index: indexApplication,
});

export const updateApplication = defineAction({
  guard: "applications:manage",
  run: async ({ userId }, id: number, input: { name: string } & Fields) => {
    need(input.name, "Application");
    return prisma.application.update({ where: { id }, data: { ...fields(input.name, input), updatedBy: userId } });
  },
  tag: CACHE_TAGS.applications,
  paths: (_c, id: number) => ["/applications", `/applications/${id}`],
  index: indexApplication,
});

export const deleteApplication = defineAction({
  guard: (id: number, reason?: string) => authorizeDeletion("APPLICATION", id, reason),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the guard reads the reason
  run: async (gate, id: number, _reason?: string): Promise<DeleteResult> => {
    if ("requested" in gate) return { ok: true, requested: true };
    const refs = await findActiveReferences("Application", id);
    if (refs.length > 0) return { ok: false, error: formatActiveReferencesError(refs) };
    await prisma.application.update({ where: { id }, data: { deletedAt: new Date(), deletedBy: gate.userId } });
    return { ok: true };
  },
  tag: CACHE_TAGS.applications,
  paths: (_o, id: number) => ["/applications", `/applications/${id}`],
  index: (o, id: number) => !o.requested && removeApplication(id),
});

export const restoreApplication = defineAction({
  guard: "trash:restore",
  run: async (_c, id: number) => {
    const row = await prisma.application.update({ where: { id }, data: { deletedAt: null, deletedBy: null } });
    await bestEffort(indexApplication(row), "reindex restored application");
  },
  tag: CACHE_TAGS.applications,
  paths: (_o, id: number) => ["/applications", `/applications/${id}`, "/admin/trash"],
});

export const createSubmodule = defineAction({
  guard: "applications:manage",
  run: async ({ userId }, appId: number, name: string) => {
    need(name, "Submodule");
    return prisma.submodule.create({ data: { appId, name: name.trim(), creator: userId } });
  },
});

export const deleteSubmodule = defineAction({
  guard: "applications:delete",
  run: async ({ userId }, id: number): Promise<{ ok: true } | { ok: false; error: string }> => {
    const refs = await findActiveReferences("Submodule", id);
    if (refs.length > 0) return { ok: false, error: formatActiveReferencesError(refs) };
    const sub = await prisma.submodule.update({ where: { id }, data: { deletedAt: new Date(), deletedBy: userId } });
    revalidatePath(`/applications/${sub.appId}`);
    return { ok: true };
  },
  paths: (_o, id: number) => [`/submodules/${id}`],
});

export const restoreSubmodule = defineAction({
  guard: "trash:restore",
  run: async (_c, id: number) => {
    const sub = await prisma.submodule.update({ where: { id }, data: { deletedAt: null, deletedBy: null } });
    revalidatePath(`/applications/${sub.appId}`);
  },
  paths: (_o, id: number) => [`/submodules/${id}`, "/admin/trash"],
});

export const createTag = defineAction({
  guard: "articles:create",
  run: async ({ userId }, name: string, kind?: string | null) => {
    need(name, "Tag");
    return prisma.tag.upsert({
      where: { name: name.trim() },
      update: {},
      create: { name: name.trim(), kind: normalizeKind(kind), creator: userId },
    });
  },
});

export const findSoleApplicationForClient = defineAction({
  guard: "articles:view",
  run: async (_c, clientId: number) => {
    const apps = await prisma.article.findMany({
      where: { clientId, appId: { not: null }, deletedAt: null },
      select: { appId: true },
      distinct: ["appId"],
    });
    return apps.length === 1 ? apps[0].appId : null;
  },
});

type ArchiveResult = { ok: true; warning?: string };
const bexioWarning = (errors: string[]) =>
  errors.length ? `Saved in the Hub, but Bexio did not accept it: ${errors.join("; ")}` : undefined;

/** Archive a client: hidden from the clients list by default, its Bexio project(s) set to Archived. New projects can still be created for it. */
export const archiveClient = defineAction({
  guard: "clients:archive",
  run: async ({ userId }, id: number): Promise<ArchiveResult> => {
    const { bexioErrors } = await setClientArchived(id, true, userId);
    return { ok: true, warning: bexioWarning(bexioErrors) };
  },
  tag: CACHE_TAGS.clients,
  paths: (_o, id: number) => ["/clients", `/clients/${id}`],
});

export const unarchiveClient = defineAction({
  guard: "clients:archive",
  run: async ({ userId }, id: number): Promise<ArchiveResult> => {
    const { bexioErrors } = await setClientArchived(id, false, userId);
    return { ok: true, warning: bexioWarning(bexioErrors) };
  },
  tag: CACHE_TAGS.clients,
  paths: (_o, id: number) => ["/clients", `/clients/${id}`],
});
